/**
 * PERSONAL SALE — pure logic (no React, no DOM beyond `sessionStorage`), so the window math, the
 * countdown format and the session-dismiss can be unit-tested in isolation.
 *
 * The feature: a **new Free-plan** account is offered a limited-time upgrade discount for the first
 * **24 hours** after it REGISTERED (`Me.recipientRegisteredAt`). A top banner counts the time DOWN to
 * zero and a "30% OFF" badge rides the drawer credits card; both vanish the moment the window closes.
 * The banner carries a session-only dismiss.
 *
 * **What the offer covers: YEARLY, on Pro and Max.** Both halves are narrow, and both narrowings live
 * here — the cadence in `shouldAttemptPersonalSaleCoupon`, the tier in `PERSONAL_SALE_PLAN_KEYS` — so
 * the picker's visual and the coupon carried into checkout can never disagree about which purchase is
 * discounted.
 *
 * The whole gate lives above this file (`lib/use-personal-sale.ts`) — this half only knows the arithmetic.
 */

import type { Cadence } from './plans';

/** The offer window: 24 hours from the anchor date. */
export const PERSONAL_SALE_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * The personal-sale discount: 30% off. This is the ONE place the rate lives, so the "30% OFF" badge/
 * banner copy and the discounted price shown on the `/packages` cards can never disagree on the number.
 *
 * The CHARGE side is the coupon `PERSONAL_SALE_COUPON` below, auto-applied at checkout — the catalog
 * coupon must carry this same 30% so what the card shows is what checkout takes.
 */
export const PERSONAL_SALE_DISCOUNT_RATE = 0.3;

/**
 * The agent-coupon (Nile) catalog code that DELIVERS the personal-sale discount at checkout. Auto-applied
 * by `pages/packages.tsx` `buyPlan` when the buyer is personal-sale eligible on a YEARLY plan and hasn't
 * entered their own promo code — see `shouldAttemptPersonalSaleCoupon`. The code is re-validated by
 * `CreateCheckoutSession`; the client pre-validates it and drops it (full price) on any rejection, so an
 * eligible buyer is never blocked by it.
 *
 * It must exist in the catalog as a 30%-off, YEARLY, multi-use, non-expiring coupon across every currency
 * the picker sells and every plan in `PERSONAL_SALE_PLAN_KEYS` (Pro/Max — NOT Basic), or an eligible buyer's
 * checkout would reject it.
 */
export const PERSONAL_SALE_COUPON = 'WELCOME30';

/**
 * The plan CARD KEYS the personal sale discounts: **Pro and Max only** (`lib/plans-server` builds the
 * cards as `basic` | `pro` | `max`; Max+ shares the `max` card). Basic is deliberately NOT on the offer
 * — it was, and a Basic buyer saw a `WELCOME30` line take 30% off their subtotal at checkout, which is
 * not the discount this promotion sells.
 *
 * An ALLOWLIST rather than a "not basic" denylist, because the two fail in opposite directions: a tier
 * added to the catalog later is off the offer until someone puts it on, instead of quietly being
 * discounted 30% because nobody remembered this file. Whatever is listed here must also be the set the
 * catalog coupon itself is scoped to — the code is re-validated at `CreateCheckoutSession`, so a
 * catalog that still accepts `WELCOME30` on Basic would take the discount for anyone reaching checkout
 * by another route.
 */
export const PERSONAL_SALE_PLAN_KEYS: readonly string[] = ['pro', 'max'];

/**
 * Whether the personal sale covers a given plan card. Drives BOTH the card's visual (the "30% OFF"
 * plate and the struck-through price) and the auto-coupon below, so a card that shows the offer is
 * exactly a card that takes it — display must match the charge. An unknown/absent key is NOT covered.
 */
export function personalSaleAppliesToPlan(planKey: string | undefined): boolean {
  return planKey !== undefined && PERSONAL_SALE_PLAN_KEYS.includes(planKey);
}

/**
 * Whether to ATTEMPT the auto personal-sale coupon for a checkout — before it is validated. True only
 * when the buyer applied NO coupon of their own (theirs always wins), is personal-sale eligible, is
 * buying the YEARLY cadence the offer discounts, is on a plan the offer covers (Pro/Max — see
 * `personalSaleAppliesToPlan`), and is on the LIVE billing lane (a coupon in the sandbox lane is
 * refused by `/api/stripe/checkout`, so never attempt one there). Pure so the rule is unit-tested
 * without React or the network. The actual apply-or-drop happens in `buyPlan`, which pre-validates the
 * code and omits it on any rejection.
 */
export function shouldAttemptPersonalSaleCoupon(args: {
  userCoupon: string | undefined;
  personalSaleActive: boolean;
  cadence: Cadence;
  planKey: string | undefined;
  liveLane: boolean;
}): boolean {
  const { userCoupon, personalSaleActive, cadence, planKey, liveLane } = args;
  return (
    !userCoupon && personalSaleActive && cadence === 'yearly' && personalSaleAppliesToPlan(planKey) && liveLane
  );
}

/**
 * A whole-unit price with the personal-sale discount applied, rounded to a whole unit (prices on the
 * cards render as whole units — "$40" → "$28"). Pure so the card's scratched-vs-discounted pair is
 * unit-tested without React.
 */
export function personalSaleDiscounted(amount: number): number {
  return Math.round(amount * (1 - PERSONAL_SALE_DISCOUNT_RATE));
}

/**
 * The banner's reserved slot height (px) — published to `--personal-sale-banner-h`, which the content
 * region insets its top by (globals.css), so the banner pushes the page header/island down and the rail
 * stays put. 56px bar (Figma) + 24px top gap; the gap BELOW comes from the page's own top padding, same
 * as the sibling credits banner's slot.
 */
export const PERSONAL_SALE_BANNER_HEIGHT_PX = 80;

/**
 * The DESKTOP `/packages` banner's reserved height (px) — published to `--personal-sale-topbar-h`. That
 * variant is a full-viewport-width blue strip flush at the top (Figma 16946-167945), mounted as a flow
 * sibling above `<main>` like the app-download banner, so it pushes the whole frame (rail + content)
 * down; `<main>` subtracts this from its height. A fixed 56px — the strip is a single non-wrapping row,
 * flush at the top with no outer gap, so unlike the in-content banner it needs no measuring.
 */
export const PERSONAL_SALE_PACKAGES_STRIP_HEIGHT_PX = 56;

/**
 * Milliseconds left in the window, floored at 0. `createdAtMs` is the anchor date's epoch ms
 * (`Me.recipientRegisteredAt` parsed); the arithmetic is the same whichever field anchors it.
 */
export function personalSaleRemainingMs(createdAtMs: number, nowMs: number): number {
  // Floor at 0 (expired) AND cap at the window: a client clock BEHIND the server (nowMs < createdAtMs,
  // from clock skew) would otherwise return more than 24h and render e.g. "25:00:00". Mirrors native.
  return Math.min(PERSONAL_SALE_WINDOW_MS, Math.max(0, createdAtMs + PERSONAL_SALE_WINDOW_MS - nowMs));
}

/**
 * The window's real anchor date: the recipient's `registeredAt` when the service has stamped one, else
 * `createdDate` — the VMR-documented fallback (wix-vmr-repo#29108: `registered_at` is absent on every
 * row written before the field existed, and deliberately not backfilled). Resolved server-side in
 * `/api/me` so the client receives ONE already-resolved field (`Me.recipientRegisteredAt`). Pure +
 * testable. Mirrors iOS/Android `resolveRegisteredAt` — keep the precedence identical.
 *
 * `registeredAt` and `createdDate` normally agree (both stamped at recipient creation) — they diverge
 * only for a site-transfer claim, where `registeredAt` MOVES to the claim instant while `createdDate`
 * stays the row's original (possibly outreach-minted, months-old) creation time.
 */
export function resolveRegisteredAt(registeredAt: Date | undefined, createdDate: Date | undefined): Date | undefined {
  return registeredAt ?? createdDate;
}

/** Whether `now` is still inside the 24h window opened at `createdAtMs`. */
export function personalSaleWithinWindow(createdAtMs: number, nowMs: number): boolean {
  return personalSaleRemainingMs(createdAtMs, nowMs) > 0;
}

/**
 * `HH:MM:SS` for the banner's counter (Figma "23:15:01"). Always two digits per field; the window is
 * 24h so hours never exceed 23, but the pad is written generally so a larger window can't render `1:2:3`.
 * A negative/zero input renders `00:00:00` — the banner is already gone by then, this is just safe.
 */
export function formatPersonalSaleCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Which banner copy to lead with. When the account is in a low/out credits state (the low/out-credits
 * widgets — daily-metered for a Free account, which every personal-sale account is), the banner leads
 * with that alert title + the offer line + counter (Figma 16946-168860); otherwise it's the plain
 * welcome banner (title + counter). Pure so the mapping is unit-tested without React.
 *
 * `level` is `useCreditsWidgetState().level`, already gated on the low/out-credits FT — so `'none'`
 * (the default banner) covers both "not low/out" and "that FT is off".
 */
export type PersonalSaleBannerCopy = { variant: 'default' } | { variant: 'creditsAlert'; titleKey: string };

export function personalSaleBannerCopy(level: 'none' | 'low' | 'out'): PersonalSaleBannerCopy {
  if (level === 'out') return { variant: 'creditsAlert', titleKey: 'personalSale.banner.outTitle' };
  if (level === 'low') return { variant: 'creditsAlert', titleKey: 'personalSale.banner.lowTitle' };
  return { variant: 'default' };
}

/**
 * Session-only dismiss. `sessionStorage` (not `localStorage`) so the banner hides for THIS tab/session
 * and returns next session if the account is still eligible — the asked-for behaviour. Every access is
 * guarded: private-mode / blocked storage simply reads as "not dismissed", so the banner shows rather
 * than a thrown error swallowing the whole frame.
 */
export const PERSONAL_SALE_DISMISS_KEY = 'orion-web:personal-sale:dismissed';

function session(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function isPersonalSaleDismissed(): boolean {
  try {
    return session()?.getItem(PERSONAL_SALE_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function setPersonalSaleDismissed(): void {
  try {
    session()?.setItem(PERSONAL_SALE_DISMISS_KEY, '1');
  } catch {
    /* storage blocked — the dismiss just doesn't persist past this render, which is harmless */
  }
}
